import { http } from '@/lib/http';
import { WORKDAY_INTEGRATION_TYPE } from './constants';
import type {
  CreateHirePayload,
  CreateHireResult,
  HireFormResponse,
} from './types';
import type { KomboEnvelope } from '../workdayPosition/types';

// `@/lib/http` is the `/api/v1` instance every other Kombo call uses; its
// interceptor already unwraps to the `{ data, errors }` envelope.

/**
 * Workday's own hire form. Same schema shape as Kombo's employee form, so the
 * existing `convertSchemaToFormConfig` renders it unchanged.
 */
const getHireForm = async (
  atsApplicationId: string | number
): Promise<KomboEnvelope<HireFormResponse>> =>
  http.get('kombo/hire_form', {
    params: {
      integration_type: WORKDAY_INTEGRATION_TYPE,
      ats_application_id: atsApplicationId,
    },
  });

/**
 * Completes the hire in Workday. Requires a position already created for the
 * application — without one it answers 422 `err_workday_position_missing`.
 *
 * Not repeatable. A repeat POST returns the stored state rather than sending
 * anything, so the backend is safe, but the UI must never invite a second
 * attempt for anything but a validation rejection.
 */
const createHire = async (
  payload: Omit<CreateHirePayload, 'integration_type'>
): Promise<KomboEnvelope<CreateHireResult>> =>
  http.post('kombo/create_hire', {
    integration_type: WORKDAY_INTEGRATION_TYPE,
    ...payload,
  });

export const WORKDAY_HIRE_API = { getHireForm, createHire };
